import { Panel, PanelProps } from 'primereact/panel';
import React from 'react';
import styled from 'styled-components';

/**
 * Shared shell for an editor sidebar. Sizing and borders differ per editor, so
 * each one wraps this with `styled()` rather than passing options.
 */
export const EditorSidebar = styled.div`
  background: var(--white-main);
  padding: 32px var(--default-padding);
  display: flex;
  flex-direction: column;
  overflow: auto;
  // ContentMediaUpload lays out in two columns down to 452px of content box
  flex: 0 0 520px;
  border-top-right-radius: var(--default-border-radius);
  border-bottom-right-radius: var(--default-border-radius);

  > *:not(:last-child) {
    margin-bottom: var(--default-padding);
  }

  > h2 {
    font-size: var(--heading-small-font-size);
    color: var(--black-main);
  }
`;

export const EditorSidebarLabel = styled.div`
  font-weight: 500;
  color: var(--black-main);
  font-size: var(--xsmall-font-size);
  margin-bottom: var(--xsmall-padding);
`;

const StyledPanel = styled(Panel)`
  .p-panel-header {
    color: var(--black-main);
    background: transparent;
    border: none;
    padding: 0;
    margin-bottom: var(--medium-padding);
  }

  .p-panel-title {
    font-weight: 600;
    font-size: var(--small-font-size);
  }

  .p-panel-content {
    padding: 0;
    border: none;
    background: transparent;
    // The panel carries its own text colour, which would otherwise override
    // whatever the surface it sits on has chosen
    color: inherit;
  }

  .p-panel-toggle-icon {
    color: var(--gray-dark);
    transition: transform 0.2s ease;
  }

  &.p-panel-collapsed .p-panel-toggle-icon {
    transform: rotate(-90deg);
  }
`;

/** A collapsible section, expanded by default, as every sidebar panel is. */
export const EditorSidebarPanel: React.FC<PanelProps> = ({
  className,
  ...props
}) => (
  <StyledPanel
    toggleable
    collapsed={false}
    collapseIcon="pi pi-chevron-up"
    expandIcon="pi pi-chevron-down"
    className={['w-full', className].filter(Boolean).join(' ')}
    {...props}
  />
);
